/**
 * HTML Sanitizer for Email Templates
 *
 * Sanitizes template HTML before save and before send:
 *   - Strips <script>, <style>, <iframe>, <object>, <embed>, <form> tags
 *   - Removes event handler attributes (onclick, onerror, onload, etc.)
 *   - Restricts link href protocols to http, https, mailto
 *   - Preserves safe email tags: p, br, strong, em, u, a, ul, ol, li,
 *     h1/h2/h3, table/tr/td/th/thead/tbody, div, span, img (src only)
 *   - Escapes dynamic variable values before insertion
 */

// Allowlisted tags for email HTML (used for documentation; stripping logic uses patterns)
// p, br, strong, b, em, i, u, s, a, ul, ol, li, h1-h6,
// table, thead, tbody, tfoot, tr, td, th, div, span, img, hr, blockquote, pre, code

// The `{0,1500}` bound equals MAX_TAG_LENGTH: a longer tag is escaped as text by rewriteTagsInner, and an unbounded
// `[^>]*` rescanned to the end of the input for every unclosed `<script` (quadratic). No whitespace is allowed after
// `<` or `</`: a browser does not read `< script>` as a tag. Besides script-like tags it lists the RAWTEXT / RCDATA and
// parser-special elements (`xmp`, `textarea`, `noscript`, ...) whose content a browser reads as text, so a `<!--` in
// them would hide markup from the scanner; removing the tag makes the content markup for both. SVG animation elements
// can set `href` to a `javascript:` URL.
const DANGEROUS_TAG_PATTERN =
  /<\/?(script|style|iframe|object|embed|form|input|applet|meta|link|base|xmp|textarea|noscript|noembed|noframes|plaintext|select|template|animate|set|animatemotion|animatetransform)\b[^>]{0,1500}>/gi;

/** A text-only `<title>` (invisible in a mail body) is dropped whole; a stray opening or closing title tag is dropped alone. */
const TITLE_BLOCK_PATTERN = /<title\b[^>]{0,1500}>[^<]{0,2000}<\/title\b[^>]{0,1500}>/gi;
const TITLE_TAG_PATTERN = /<\/?title\b[^>]{0,1500}>/gi;

// `(?<!\s)` starts each match at the beginning of a whitespace run, so a long run cannot backtrack quadratically.
const EVENT_HANDLER_PATTERN = /(?<!\s)\s+on\w+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]*)/gi;

const JAVASCRIPT_PROTOCOL_PATTERN = /(?<!\s)\s+href\s*=\s*(?:"javascript:[^"]*"|'javascript:[^']*')/gi;
const DATA_PROTOCOL_HREF_PATTERN = /(?<!\s)\s+href\s*=\s*(?:"data:[^"]*"|'data:[^']*')/gi;
const JAVASCRIPT_SRC_PATTERN = /(?<!\s)\s+src\s*=\s*(?:"javascript:[^"]*"|'javascript:[^']*')/gi;
const DATA_SRC_NON_IMAGE_PATTERN = /(?<!\s)\s+src\s*=\s*(?:"data:(?!image\/)[^"]*"|'data:(?!image\/)[^']*')/gi;

const MAX_TAG_LENGTH = 1500;
const MAX_CLOSING_TAG_LENGTH = 200;
const MAX_CONDITIONAL_DEPTH = 3;
const URL_ATTRIBUTES = new Set(['href', 'src', 'action', 'formaction', 'xlink:href', 'poster', 'background']);
/** `srcset` is a list of URLs: drop it when any candidate carries a script or data scheme. */
const UNSAFE_SRCSET = /(?:javascript|vbscript|data):/;
const EVENT_ATTRIBUTE = /^on[a-z]+$/;
/** Script-bearing CSS (`url(javascript:...)`, `expression(...)`) as a browser would read it, comments and escapes aside. */
const UNSAFE_STYLE = /(?:javascript|vbscript):|expression\(/;
const NAMED_ENTITIES: Record<string, string> = {
  colon: ':', tab: '\t', newline: '\n', amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ', sol: '/', lpar: '(', rpar: ')',
};

/** Numeric references may carry any number of leading zeros (`&#00000000106;`). */
function decodeEntities(value: string): string {
  return value.replace(/&(?:#x0*([0-9a-f]{1,8})|#0*(\d{1,10})|([a-z]{2,8}));?/gi, (m, hex?: string, dec?: string, named?: string) => {
    if (hex !== undefined || dec !== undefined) {
      const code = hex !== undefined ? parseInt(hex, 16) : parseInt(dec as string, 10);
      return code > 0 && code <= 0x10ffff ? String.fromCodePoint(code) : '';
    }
    return NAMED_ENTITIES[(named as string).toLowerCase()] ?? m;
  });
}

/** URL scheme of an attribute value as a browser would see it (entities decoded, tab/newline/control chars ignored). */
function effectiveUrl(rawValue: string): string {
  const unquoted = rawValue.replace(/^["']|["']$/g, '');
  // eslint-disable-next-line no-control-regex
  return decodeEntities(unquoted).replace(/[\u0000-\u001f\u007f-\u009f\s]+/g, '').toLowerCase();
}

function isBlockedUrl(attrName: string, rawValue: string): boolean {
  const url = effectiveUrl(rawValue);
  if (/^(?:javascript|vbscript):/.test(url)) return true;
  if (url.startsWith('data:')) return !(attrName === 'src' && url.startsWith('data:image/'));
  return false;
}

interface RewriteResult {
  html: string;
  /** javascript:/vbscript:/data: URLs neutralized in href/src/action-style attributes. */
  unsafeUrls: number;
  /** on* event-handler attributes removed (including slash- or quote-separated ones). */
  eventHandlers: number;
  /** Tags too long to scan that were escaped as text. */
  escapedTags: number;
}

const isLetter = (c: number): boolean => (c >= 65 && c <= 90) || (c >= 97 && c <= 122);
/** HTML whitespace only: JS `\s` also matches VT, NBSP and U+2028, which browsers do not treat as separators. */
const isSpace = (c: number): boolean => c === 32 || c === 9 || c === 10 || c === 12 || c === 13;

interface AttributeToken {
  /** Start of the separators (whitespace, `/`) before the name. */
  sepFrom: number;
  nameFrom: number;
  nameTo: number;
  /** Raw value including its quotes; -1 when the attribute has none. */
  valueFrom: number;
  valueTo: number;
  /** End of the whole attribute. */
  to: number;
}

interface TagToken {
  /** Index of the closing `>`. */
  end: number;
  /** End of the tag name. */
  nameEnd: number;
  attrs: AttributeToken[];
}

/**
 * Tokenize the tag whose `<` is at `start`, following the HTML tokenizer: the first character of an attribute name is
 * always part of it (even `=`), later name characters run to whitespace, `/`, `>` or `=` (quotes and `<` included), a
 * value is quoted only right after `=`, and an unquoted value runs to whitespace or `>`. Returns undefined when the tag
 * does not end within MAX_TAG_LENGTH or has an unterminated quote (a browser drops such a tag; it is escaped here).
 * One tokenizer decides both where a tag ends and what its attributes are, so they cannot disagree.
 */
function readTag(html: string, start: number): TagToken | undefined {
  const limit = Math.min(html.length, start + MAX_TAG_LENGTH);
  let i = start + 1;
  while (i < limit) {
    const c = html.charCodeAt(i);
    if (isSpace(c) || c === 47 || c === 62) break;
    i++;
  }
  const nameEnd = i;
  const attrs: AttributeToken[] = [];
  let sepFrom = i;
  while (i < limit) {
    while (i < limit && (isSpace(html.charCodeAt(i)) || html.charCodeAt(i) === 47)) i++;
    if (i >= limit) return undefined;
    if (html.charCodeAt(i) === 62) return { end: i, nameEnd, attrs };
    const nameFrom = i;
    i++;
    while (i < limit) {
      const c = html.charCodeAt(i);
      if (isSpace(c) || c === 47 || c === 62 || c === 61) break;
      i++;
    }
    const nameTo = i;
    let j = i;
    while (j < limit && isSpace(html.charCodeAt(j))) j++;
    let valueFrom = -1;
    let valueTo = -1;
    if (j < limit && html.charCodeAt(j) === 61) {
      j++;
      while (j < limit && isSpace(html.charCodeAt(j))) j++;
      if (j >= limit) return undefined;
      const quote = html.charCodeAt(j);
      valueFrom = j;
      if (quote === 34 || quote === 39) {
        let k = j + 1;
        while (k < limit && html.charCodeAt(k) !== quote) k++;
        if (k >= limit) return undefined;
        j = k + 1;
      } else {
        while (j < limit && !isSpace(html.charCodeAt(j)) && html.charCodeAt(j) !== 62) j++;
      }
      valueTo = j;
      i = j;
    }
    attrs.push({ sepFrom, nameFrom, nameTo, valueFrom, valueTo, to: i });
    sepFrom = i;
  }
  return undefined;
}

/** Drop event handlers and script-bearing styles, and neutralize dangerous URLs, in one tokenized opening tag. */
function rewriteTag(html: string, start: number, tag: TagToken, result: RewriteResult): string {
  let out = html.slice(start, tag.nameEnd);
  let cursor = tag.nameEnd;
  for (const attr of tag.attrs) {
    const name = html.slice(attr.nameFrom, attr.nameTo);
    const lower = name.toLowerCase();
    const value = attr.valueFrom >= 0 ? html.slice(attr.valueFrom, attr.valueTo) : undefined;
    cursor = attr.to;
    if (EVENT_ATTRIBUTE.test(lower)) {
      result.eventHandlers++;
      continue;
    }
    if (value !== undefined && lower === 'style' && UNSAFE_STYLE.test(effectiveUrl(value).replace(/\/\*.*?\*\//g, ''))) {
      result.unsafeUrls++;
      continue;
    }
    const prefix = `${html.slice(attr.sepFrom, attr.nameFrom)}${name}${html.slice(attr.nameTo, attr.valueFrom)}`;
    if (value !== undefined && lower === 'srcset' && UNSAFE_SRCSET.test(effectiveUrl(value))) {
      result.unsafeUrls++;
      out += `${prefix}""`;
      continue;
    }
    if (value !== undefined && URL_ATTRIBUTES.has(lower) && isBlockedUrl(lower, value)) {
      result.unsafeUrls++;
      out += `${prefix}"${lower === 'src' || lower === 'poster' || lower === 'background' ? '' : '#'}"`;
      continue;
    }
    out += html.slice(attr.sepFrom, attr.to);
  }
  return out + html.slice(cursor, tag.end + 1);
}

function rewriteTagsInner(html: string, result: RewriteResult, depth: number): string {
  const lastGt = html.lastIndexOf('>');
  let nextGt = -1;
  // Cached positions of the next `-->` / `--!>` (-1: none left), so each comment does not rescan the remainder.
  let plainClose = -2;
  let bangClose = -2;
  let out = '';
  let i = 0;
  while (i < html.length) {
    const lt = html.indexOf('<', i);
    // No `>` left after this `<`: nothing can close a tag, so the rest is plain text (`if a<b`).
    if (lt === -1 || lt > lastGt) {
      out += html.slice(i);
      break;
    }
    out += html.slice(i, lt);
    if (nextGt < lt) nextGt = html.indexOf('>', lt + 1);
    const c = html.charCodeAt(lt + 1);
    if (c === 33 && html.startsWith('<!--', lt)) {
      // A browser ends a comment at `-->` or `--!>`, and `<!-->` / `<!--->` are empty comments.
      let innerEnd: number;
      let end: number;
      if (html.startsWith('>', lt + 4)) {
        innerEnd = lt + 4;
        end = lt + 5;
      } else if (html.startsWith('->', lt + 4)) {
        innerEnd = lt + 4;
        end = lt + 6;
      } else {
        if (plainClose !== -1 && plainClose < lt + 4) plainClose = html.indexOf('-->', lt + 4);
        if (bangClose !== -1 && bangClose < lt + 4) bangClose = html.indexOf('--!>', lt + 4);
        const plain = plainClose;
        const bang = bangClose;
        if (plain === -1 && bang === -1) {
          // Never closes: a browser would swallow the rest as one comment, but an Outlook conditional or the layout that
          // follows would not. Escape the opener so the rest is read as markup and sanitized.
          result.escapedTags++;
          out += '&lt;';
          i = lt + 1;
          continue;
        }
        const useBang = bang !== -1 && (plain === -1 || bang < plain);
        innerEnd = useBang ? bang : plain;
        end = innerEnd + (useBang ? 4 : 3);
      }
      const inner = html.slice(lt + 4, innerEnd);
      if (inner.startsWith('[if') && depth >= MAX_CONDITIONAL_DEPTH) {
        // Too deep to sanitize: escape the opener so what follows is read as markup (and sanitized) instead.
        result.escapedTags++;
        out += '&lt;';
        i = lt + 1;
        continue;
      }
      // Outlook conditionals (`<!--[if mso]> ... <![endif]-->`) hide real markup: sanitize it; other comments are inert.
      out += inner.startsWith('[if') ? `<!--${rewriteTagsInner(inner, result, depth + 1)}-->` : html.slice(lt, end);
      i = end;
      continue;
    }
    if (c === 47 && isLetter(html.charCodeAt(lt + 2))) {
      // An end tag takes attributes too (`</a b='>'>`): find its end with the tag tokenizer, not the first `>`.
      const endTag = nextGt !== -1 && nextGt - lt < MAX_TAG_LENGTH ? readTag(html, lt + 1) : undefined;
      if (endTag) {
        out += html.slice(lt, endTag.end + 1);
        i = endTag.end + 1;
      } else {
        result.escapedTags++;
        out += '&lt;';
        i = lt + 1;
      }
      continue;
    }
    if (html.startsWith('<![CDATA[', lt)) {
      // In SVG/MathML a browser reads this as a CDATA section running to `]]>`, which would swallow whatever follows
      // (the layout footer); in HTML it is a bogus comment. Nothing in an email needs it: show it as text.
      result.escapedTags++;
      out += '&lt;';
      i = lt + 1;
      continue;
    }
    if (c === 33 || c === 47 || c === 63) {
      // <!doctype ...>, `</` + non-letter and `<?...>` are bogus comments a browser ends at the first `>`: pass through when they fit the bound, otherwise they are unscannable.
      if (nextGt !== -1 && nextGt - lt < MAX_CLOSING_TAG_LENGTH) {
        out += html.slice(lt, nextGt + 1);
        i = nextGt + 1;
      } else {
        result.escapedTags++;
        out += '&lt;';
        i = lt + 1;
      }
      continue;
    }
    if (!isLetter(c)) {
      out += '<';
      i = lt + 1;
      continue;
    }
    const tag = nextGt !== -1 && nextGt - lt < MAX_TAG_LENGTH ? readTag(html, lt) : undefined;
    if (!tag) {
      // Starts like a tag, has a `>` later, but does not fit the bound (or has an unterminated quote): escape it.
      result.escapedTags++;
      out += '&lt;';
      i = lt + 1;
      continue;
    }
    out += rewriteTag(html, lt, tag, result);
    i = tag.end + 1;
  }
  return out;
}

/**
 * Neutralize dangerous URLs and event handlers inside opening tags, in any obfuscated form, and escape a tag that is
 * too long to scan. Single linear pass: a `<` with no `>` after it is plain text and is left alone.
 */
function rewriteTags(html: string): RewriteResult {
  const result: RewriteResult = { html: '', unsafeUrls: 0, eventHandlers: 0, escapedTags: 0 };
  result.html = rewriteTagsInner(html, result, 0);
  return result;
}

function neutralizeUnsafeUrls(html: string): string {
  return rewriteTags(html).html;
}

function sanitizeHtmlOnce(html: string): string {
  let sanitized = html;

  sanitized = sanitized.replace(DANGEROUS_TAG_PATTERN, '');
  sanitized = sanitized.replace(TITLE_BLOCK_PATTERN, '').replace(TITLE_TAG_PATTERN, '');

  sanitized = sanitized.replace(EVENT_HANDLER_PATTERN, '');

  sanitized = sanitized.replace(JAVASCRIPT_PROTOCOL_PATTERN, ' href="#"');
  sanitized = sanitized.replace(DATA_PROTOCOL_HREF_PATTERN, ' href="#"');
  sanitized = sanitized.replace(JAVASCRIPT_SRC_PATTERN, ' src=""');
  sanitized = sanitized.replace(DATA_SRC_NON_IMAGE_PATTERN, ' src=""');
  sanitized = neutralizeUnsafeUrls(sanitized);

  return sanitized;
}

const MAX_SANITIZE_PASSES = 5;

/**
 * Sanitize email template HTML body.
 * Removes dangerous elements and attributes while preserving safe formatting. Removing a tag can splice its
 * neighbours into a new one (`<scr<script>ipt>`), so it repeats until the output is stable; input that never settles
 * is escaped as text.
 */
export function sanitizeHtml(html: string): string {
  if (!html) return html;

  let current = html;
  for (let pass = 0; pass < MAX_SANITIZE_PASSES; pass++) {
    const next = sanitizeHtmlOnce(current);
    if (next === current) return current;
    current = next;
  }
  return current.replace(/</g, '&lt;');
}

/**
 * Escape a dynamic value before inserting into HTML body.
 * Prevents XSS from user-controlled data like patient names.
 */
export function escapeHtmlValue(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Check if HTML contains unsafe content and return issues.
 * Used for validation warnings in the template editor.
 */
export interface HtmlSanitizationIssue {
  type: 'error' | 'warning';
  message: string;
}

export function validateHtmlSafety(html: string): HtmlSanitizationIssue[] {
  const issues: HtmlSanitizationIssue[] = [];
  const rewrite = rewriteTags(html);

  if (DANGEROUS_TAG_PATTERN.test(html)) {
    DANGEROUS_TAG_PATTERN.lastIndex = 0;
    issues.push({ type: 'error', message: 'Template contains unsafe tags (script, style, iframe, form, input, etc.). Remove them before saving.' });
  }

  if (EVENT_HANDLER_PATTERN.test(html) || rewrite.eventHandlers > 0) {
    EVENT_HANDLER_PATTERN.lastIndex = 0;
    issues.push({ type: 'error', message: 'Template contains event handler attributes (onclick, onerror, etc.). Remove them before saving.' });
  }

  if (JAVASCRIPT_PROTOCOL_PATTERN.test(html) || rewrite.unsafeUrls > 0) {
    JAVASCRIPT_PROTOCOL_PATTERN.lastIndex = 0;
    issues.push({ type: 'error', message: 'Template contains unsafe URLs or styles (javascript:, vbscript:, data:, expression()). Use https: or mailto: links instead.' });
  }

  if (rewrite.escapedTags > 0) {
    issues.push({ type: 'warning', message: 'A tag too long to scan was escaped as text (it will not render as HTML).' });
  }

  if (DATA_PROTOCOL_HREF_PATTERN.test(html)) {
    DATA_PROTOCOL_HREF_PATTERN.lastIndex = 0;
    issues.push({ type: 'error', message: 'Template contains data: URLs in links. Use https: or mailto: links instead.' });
  }

  return issues;
}

/**
 * Returns true if the HTML is safe (no dangerous patterns found).
 */
export function isHtmlSafe(html: string): boolean {
  return validateHtmlSafety(html).length === 0;
}
